// HomeSelect.js
// Dropdown-menu select: a bordered pill trigger (matching ClassSwitcher's
// existing pill) that opens a header/HeaderDropdown card of
// header/HeaderMenuItem rows, one per item, with a check mark beside the
// selected key. Replaces the native @react-native-picker/picker used for
// the class switcher and the teacher "preview as" control.
// See docs/superpowers/specs/2026-09-16-home-redesign.md §11 (WP9).
import React, { useState } from "react";
import { Pressable, Text, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import HeaderDropdown, { HeaderMenuItem } from "../../header/HeaderDropdown";
import { homeColors } from "../homeTheme";

const identity = (s) => s;

// HeaderMenuItem has no separate sublabel row; fold it into the label with
// the same " · " separator used elsewhere on this page (e.g. ClassSwitcher's
// own class-name-and-count pill).
const itemLabel = (item) => (item.sublabel ? `${item.label} · ${item.sublabel}` : item.label);

const isSelected = (item, selectedKey) => String(item.key) === String(selectedKey);

const HomeSelect = ({
  icon = null,
  label = null,
  valueLabel,
  items = [],
  selectedKey = null,
  onChange,
  align = "right",
  width = 280,
  isDarkMode = false,
  t = identity,
  testID,
}) => {
  const [expanded, setExpanded] = useState(false);
  const colors = homeColors(isDarkMode);
  const close = () => setExpanded(false);

  const triggerText = label ? `${label}: ${valueLabel}` : valueLabel;

  // Nothing (or exactly one thing) to switch between: render a static,
  // non-pressable pill, the same idiom ClassSwitcher already uses for a
  // single class.
  if (!Array.isArray(items) || items.length <= 1) {
    return (
      <Pressable
        testID={testID ? `${testID}-static` : undefined}
        style={[styles.pill, { borderColor: colors.line, backgroundColor: colors.cardBg }]}
      >
        {icon ? (
          <MaterialCommunityIcons name={icon} size={16} color={colors.textSecondary} />
        ) : null}
        <Text style={[styles.labelText, { color: colors.textPrimary }]}>{triggerText}</Text>
      </Pressable>
    );
  }

  const trigger = (
    <Pressable
      testID={testID ? `${testID}-toggle` : undefined}
      onPress={() => setExpanded((prev) => !prev)}
      accessibilityRole="button"
      accessibilityState={{ expanded }}
      style={({ hovered }) => [
        styles.pill,
        { borderColor: colors.line, backgroundColor: colors.cardBg },
        hovered ? { backgroundColor: colors.background } : null,
      ]}
    >
      {icon ? (
        <MaterialCommunityIcons name={icon} size={16} color={colors.textSecondary} />
      ) : null}
      <Text style={[styles.labelText, { color: colors.textPrimary }]}>{triggerText}</Text>
      <MaterialCommunityIcons
        name={expanded ? "chevron-up" : "chevron-down"}
        size={16}
        color={colors.textSecondary}
      />
    </Pressable>
  );

  return (
    <HeaderDropdown
      open={expanded}
      onClose={close}
      isDarkMode={isDarkMode}
      align={align}
      width={width}
      maxHeight={320}
      trigger={trigger}
      testID={testID}
    >
      {items.map((item) => (
        <HeaderMenuItem
          key={String(item.key)}
          isDarkMode={isDarkMode}
          icon={item.icon || null}
          label={itemLabel(item)}
          onPress={() => {
            onChange?.(item.key);
            close();
          }}
          right={
            isSelected(item, selectedKey) ? (
              <MaterialCommunityIcons name="check" size={18} color={colors.brand} />
            ) : null
          }
          testID={testID ? `${testID}-item-${item.key}` : undefined}
        />
      ))}
    </HeaderDropdown>
  );
};

const styles = StyleSheet.create({
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    borderWidth: 1,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  labelText: { fontSize: 13, fontWeight: "600" },
});

export default HomeSelect;
